import { redirect, permanentRedirect } from 'next/navigation';
import { getLocale, Locale } from '@libs/core';
import { getENV } from './env';

const getHost = () => {
    const locale = getLocale();
    const env = getENV('NEXT_PUBLIC_APP_ENV');
    const domain = locale === Locale['en-US'] ? 'com' : 'ca';

    const hosts = {
        dev: 'http://localhost:3000',
        test: `https://test.pilot.petsmart.${domain}`,
        qa: `https://development.pilot.petsmart.${domain}`,
        stage: `https://staging.pilot.petsmart.${domain}`,
        prod: `https://www.petsmart.${domain}`,
    };

    return hosts[env];
};

/**
 * Redirects to a PetSmart URL with a 308 permanent redirect, automatically
 * resolving the correct domain based on locale and environment.
 *
 * @param url - The target URL path (relative) or full URL (absolute)
 * @returns Never returns - triggers a 308 permanent redirect
 *
 * @example
 * ```typescript
 * // Redirects to https://www.petsmart.com/dog in production (en-US)
 * internalPermanentDirect('/dog');
 *
 * // Redirects to https://test.pilot.petsmart.ca/cat in test (en-CA)
 * internalPermanentDirect('/cat');
 * ```
 */
export const internalPermanentDirect = (url: string) => {
    permanentRedirect(new URL(url, getHost()).toString());
};

/**
 * Redirects to a PetSmart URL with a 307 temporary redirect, automatically
 * resolving the correct domain based on locale and environment.
 *
 * @param url - The target URL path (relative) or full URL (absolute)
 * @returns Never returns - triggers a 308 permanent redirect
 *
 * @example
 * ```typescript
 * // Redirects to https://www.petsmart.com/dog in production (en-US)
 * internapTemporaryRedirect('/dog');
 *
 * // Redirects to https://test.pilot.petsmart.ca/cat in test (en-CA)
 * internapTemporaryRedirect('/cat');
 * ```
 */
export const internalTemporaryRedirect = (url: string) => {
    redirect(new URL(url, getHost()).toString());
};
